import { Context } from "../models/Context";
import { getBuildNumber, getStorage, getVersion, setBuildNumber, setStorage, setVersion } from "./storage";
import { EncryptDecryptService } from "./EncryptionService";

const edService = new EncryptDecryptService();
let token = "";

function extractHeaders(response: Response) {
  return response.headers;
}

/** Plain JSON on localhost/local mode so Network tab shows field errors. */
function isLocalDevelopment() {
  try {
    const mode = String((window as any)?.config?.mode || "").toLowerCase();
    if (mode === "local" || mode === "development" || mode === "dev") return true;
    const host = window.location?.hostname || "";
    if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") return true;
  } catch {
    /* noop */
  }
  return false;
}

async function handleVersioning(header: Headers) {
  try {
    const apiVersion = header.get("X-Version") || "";
    const apiBuildNumber = header.get("X-Buildnumber") || "";
    const isLogout = header.get("X-Access") || "";

    const curVersion = getVersion();
    const buildNumber = getBuildNumber();

    if (curVersion && buildNumber) {
      if (apiVersion !== curVersion || apiBuildNumber !== buildNumber) {
        setVersion(apiVersion);
        setBuildNumber(apiBuildNumber);
        caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
        window.location.reload();
        if (isLogout === "true") setStorage(new Context());
      }
    } else {
      setVersion(apiVersion);
      setBuildNumber(apiBuildNumber);
      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
      window.location.reload();
    }
  } catch (e) {
    /* noop */
  }
}

/** Decrypt `_payload` into the original API body (`{ data, message, error }`). */
async function unwrapEncryptedBody(body: any) {
  if (!body || !body._payload) return body;

  const encValue = body._payload;
  let dec: string | undefined = undefined;

  if (typeof (edService as any).decryptServerFormat === "function") {
    dec = await (edService as any).decryptServerFormat(encValue, edService.EncryptKey);
  }
  if (dec === undefined && typeof (edService as any).decryptFromBackend === "function") {
    dec = await (edService as any).decryptFromBackend(encValue, edService.EncryptKey);
  }
  if (dec === undefined) {
    const decrypted = edService.decryptToObject(encValue);
    return decrypted !== undefined ? decrypted : body;
  }

  try {
    return JSON.parse(dec);
  } catch {
    return dec;
  }
}

function formatFieldErrors(errors: any) {
  if (!errors || typeof errors !== "object") return "";
  return Object.entries(errors)
    .flatMap(([field, msgs]) => {
      const list = Array.isArray(msgs) ? msgs : [msgs];
      return list.map((m) => `${field}: ${m}`);
    })
    .filter(Boolean)
    .join("\n");
}

function extractApiError(body: any, statusText: string) {
  if (body == null) return statusText;
  if (typeof body === "string") return body;
  const fieldErrors = formatFieldErrors(body.errors);
  if (fieldErrors) return fieldErrors;
  if (body.message) return body.message;
  if (body.error) return body.error;
  if (body.data) return body.data;
  return body;
}

export const POST_ENC = async (
  url: string,
  data: any = null,
  isFormData: boolean = false,
  fileToken: string = "",
  sToken: string = "",
  encryptResponse: boolean = true,
  encryptRequest: boolean = true
) => {
  try {
    const encryptReq = !isLocalDevelopment() && encryptRequest;
    const encryptRes = !isLocalDevelopment() && encryptResponse;
    const localStorage = getStorage();
    if (localStorage?.loginDetail) token = localStorage.loginDetail.token || "";

    const headers: any = {};
    if (isFormData) {
      headers.Authorization = token ? `Bearer ${token}` : "";
      if (sToken) headers["s-token"] = sToken;
      headers.Accept = "*/*";
    } else {
      headers.Authorization = token ? `Bearer ${token}` : "";
      if (sToken) headers["s-token"] = sToken;
      headers.Accept = "application/json";
      headers["Content-Type"] = "application/json";
    }

    if (encryptReq || encryptRes) headers["X-Encrypt-Request"] = "true";
    if (fileToken) headers.Authorization = fileToken ? `Bearer ${fileToken}` : "";

    let bodyToSend: any = null;
    if (!isFormData) {
      if (encryptReq) {
        try {
          if (typeof (edService as any).encryptServerFormat === "function") {
            const enc = await (edService as any).encryptServerFormat(data, edService.EncryptKey);
            bodyToSend = JSON.stringify({ _payload: enc });
          } else if (typeof (edService as any).encryptForBackend === "function") {
            const enc = await (edService as any).encryptForBackend(data, edService.EncryptKey);
            bodyToSend = JSON.stringify({ _payload: enc });
          } else {
            const enc = edService.encryptObject(data);
            bodyToSend = JSON.stringify({ _payload: enc });
          }
        } catch (e) {
          bodyToSend = JSON.stringify(data);
        }
      } else {
        bodyToSend = JSON.stringify(data);
      }
    } else {
      bodyToSend = data;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: isFormData ? data : bodyToSend,
    });

    const header = extractHeaders(response);
    await handleVersioning(header);

    const body = await response.json();
    const responseJson: any = { ok: response.ok, status: response.status, statusText: response.statusText, header, data: body };

    if (responseJson.data && responseJson.data._payload) {
      try {
        responseJson.data = await unwrapEncryptedBody(responseJson.data);
      } catch (e) {
        /* keep original body */
      }
    }

    if (!responseJson.ok) {
      throw extractApiError(responseJson.data, responseJson.statusText);
    }

    // Same shape as plain POST: `{ data, message, error }` — do not drop one `.data` level.
    return responseJson.data;
  } catch (error) {
    throw error;
  }
};

export const GET_ENC = async (
  url: string,
  params: any = null,
  signal?: AbortSignal,
  sToken?: string,
  encryptResponse: boolean = true
) => {
  try {
    const useEncryption = !isLocalDevelopment() && encryptResponse;
    const localStorage = getStorage();
    if (localStorage?.loginDetail) token = localStorage.loginDetail.token || "";

    const headers: any = {
      Authorization: token ? `Bearer ${token}` : "",
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    if (sToken) headers["s-token"] = sToken;
    if (useEncryption) headers["X-Encrypt-Request"] = "true";

    const response = await fetch(url, { method: "GET", headers, signal });
    const header = extractHeaders(response);
    await handleVersioning(header);
    const body = await response.json();
    const responseJson: any = { ok: response.ok, status: response.status, statusText: response.statusText, header, data: body };

    if (responseJson.data && responseJson.data._payload) {
      try {
        responseJson.data = await unwrapEncryptedBody(responseJson.data);
      } catch (e) {
        /* keep original body */
      }
    }

    if (!responseJson.ok) {
      throw extractApiError(responseJson.data, responseJson.statusText);
    }

    return responseJson;
  } catch (error) {
    throw error;
  }
};
